import { sevenroomsVenueGroupSchema } from '@models/schema'
import { SevenroomsVenueGroup } from '@models/venue/integrations/SevenroomsAudience'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createSevenroomsVenueGroup as createSevenroomsVenueGroupAPI,
  fetchSevenroomsVenueGroups,
} from '@services/venue/integrations/SevenroomsVenueGroupService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<SevenroomsVenueGroup> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'sevenroomsVenueGroup',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getSevenroomsVenueGroupsSuccess,
  setQuery: setSevenroomsVenueGroupQuery,
  reset: resetSevenroomsVenueGroupState,
  resetQueryAndIds: resetSevenroomsVenueGroupQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: SevenroomsVenueGroup }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectSevenroomsVenueGroups = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedSevenroomsVenueGroup = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectSevenroomsVenueGroupById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectSevenroomsVenueGroupsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedSevenroomsVenueGroup =
  (sevenroomsVenueGroup: SevenroomsVenueGroup | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<SevenroomsVenueGroup>(sevenroomsVenueGroup)
      )
    )

export const getSevenroomsVenueGroups = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchSevenroomsVenueGroups(params),
    [sevenroomsVenueGroupSchema],
    slice.actions
  )

export const createSevenroomsVenueGroup = (
  sevenroomsVenueGroup: SevenroomsVenueGroup
) =>
  SBAPI.CreateDispatched(
    createSevenroomsVenueGroupAPI(sevenroomsVenueGroup),
    sevenroomsVenueGroupSchema,
    slice.actions
  )
